import { Component, inject, Input, OnInit } from '@angular/core';
import { CommonModule } from '@angular/common';
import { RouterModule } from '@angular/router';
import { EQuantityValidationResult } from 'projects/shared/lib/enum/quantity/quantity-validation-result.enum';
import { EToastItemType } from 'projects/shared/lib/enum/toast/toast-entry-type.enum';
import { LoggerService } from 'projects/shared/lib/services/infrastructure/logger/logger.service';
import { EError, ShoppingCartDto, ShoppingCartItemDto } from 'projects/shared/lib/services/INTEGRA_Shop_Client';
import { ToastItem } from 'projects/shared/lib/components/shared/toast/item/toast-item.model';
import { CartPreviewItem } from './cart-preview-item.model';
import { QuantityInputBareChangeResult } from 'projects/shared/lib/components/shared/base/quantity-input/quantity-input-bare/change-result/quantity-input-bare-change-result.model';
import { ServiceLocator } from 'projects/shared/lib/services/infrastructure/service-locator.service';
import { ICartService, ICartServiceToken } from 'projects/shared/lib/services/data/cart/cart-service.interface';
import { ENVIRONMENT } from 'projects/shared/lib/environment/environment';
import { Subject, firstValueFrom } from 'rxjs';
import { ErrorCodeService } from 'projects/shared/lib/services/infrastructure/error-code.service';
import { IConfigurationServiceToken } from 'projects/shared/lib/services/infrastructure/configuration/configuration-service.interface';
import { IComparisonService, IComparisonServiceToken } from 'projects/shared/lib/services/infrastructure/comparison/comparison-service.interface';
import { ITooltipService, ITooltipServiceToken } from 'shared/services/ui/tooltip/tooltip-service.interface';
import { IToastService, IToastServiceToken } from 'shared/services/ui/toast/toast-service.interface';
import { QuantityInputBareComponent } from 'shared/components/shared/base/quantity-input/quantity-input-bare/quantity-input-bare.component';
import { PriceFormatComponent } from 'shared/components/shared/price/format/price-format.component';
import { ProductAvailabilityIconComponent } from 'shared/components/shared/product/availability/icon/product-availability-icon.component';
import { ProductDeleteIconComponent } from 'shared/components/shared/product/delete-icon/product-delete-icon.component';

@Component({
  selector: 'app-cart-preview-item',
  imports: [
    CommonModule,
    RouterModule,
    QuantityInputBareComponent,
    PriceFormatComponent,
    ProductDeleteIconComponent,
    ProductAvailabilityIconComponent,
  ],
  templateUrl: './cart-preview-item.component.html',
  styleUrls: ['./cart-preview-item.component.scss']
})
export class CartPreviewItemComponent implements OnInit {
  @Input() public cartPreviewItem?: CartPreviewItem | undefined;

  public deleteIconTooltipHtmlContent: string | undefined;
  private shouldRemoveProductFromCartUponNegativeQuantity: boolean | undefined;
  public acceptQuantityChangeTrigger: Subject<void>;
  public rejectQuantityChangeTrigger: Subject<void>;

  public cartPreviewItemDisplayAvailabilityIcon: boolean;
  public cartPreviewItemDisplayPrice: boolean;
  public cartPreviewItemDisplayDiscounts: boolean;

  private logger: LoggerService;
  private readonly cartService: ICartService;
  private readonly toastService: IToastService = inject(IToastServiceToken);
  private readonly toolTipService: ITooltipService = inject(ITooltipServiceToken);
  private readonly comparisonService: IComparisonService;

  public constructor() {
    this.logger = ServiceLocator.injector.get(LoggerService);
    this.cartService = inject(ICartServiceToken);
    this.comparisonService = inject(IComparisonServiceToken);
    this.getConfigurationAsync();
    this.acceptQuantityChangeTrigger = new Subject<void>();
    this.rejectQuantityChangeTrigger = new Subject<void>();

    this.cartPreviewItemDisplayAvailabilityIcon = ENVIRONMENT.cart.cartPreview?.cartPreviewItemAvailabilityIcon ?? true;
    this.cartPreviewItemDisplayPrice = ENVIRONMENT.cart.cartPreview?.cartPreviewItemDisplayPrice ?? true;
    this.cartPreviewItemDisplayDiscounts = ENVIRONMENT.cart.cartPreview?.cartPreviewItemDisplayDiscounts ?? true;
  }

  public ngOnInit(): void {
    if (this.cartPreviewItem == undefined) {
      this.logger.info(`Missing model in CartPreviewItem`, this.constructor.name);
      return;
    }

    if (this.cartPreviewItem.productIdentifier == undefined) {
      this.logger.info(`Missing productIdentifier in CartPreviewItem`, this.constructor.name);
      return;
    }
    this.deleteIconTooltipHtmlContent = this.toolTipService.deleteFromCartIconToolTipGet();
  }

  public async getConfigurationAsync() {
    this.shouldRemoveProductFromCartUponNegativeQuantity = await firstValueFrom(ServiceLocator.injector.get(IConfigurationServiceToken).shouldRemoveProductFromCartUponNegativeQuantityGet());
  }

  public quantityChanged(quantityChangeResult: QuantityInputBareChangeResult) {
    if (this.cartPreviewItem == undefined) {
      return;
    }
    if (this.cartPreviewItem.productIdentifier == undefined) {
      return;
    }
    if (this.cartPreviewItem.shoppingCartItem == undefined) {
      return;
    }

    let hasQuantityChanged = this.cartPreviewItem.shoppingCartItem.quantity != quantityChangeResult;
    if (hasQuantityChanged == false) {
      return;
    }

    let cartItemCopy = new ShoppingCartItemDto(this.cartPreviewItem.shoppingCartItem);

    let quantity = quantityChangeResult.valueCurrent;
    switch (quantityChangeResult.validationResult) {
      case undefined:

        break;

      case EQuantityValidationResult.Valid:
        if (quantity == undefined) {
          return;
        }

        cartItemCopy.quantity = quantity;

        this.updateItemInCart(cartItemCopy);
        break;

      case EQuantityValidationResult.BelowMinium:
        if (this.shouldRemoveProductFromCartUponNegativeQuantity) {
          this.deleteItem();
        }
        break;

      case EQuantityValidationResult.AboveMaximum:
        throw new Error("Missing implementation: quantity input above maximum");

      default:
        break;
    }
  }

  private updateItemInCart(cartItemCopy: ShoppingCartItemDto) {
    if (cartItemCopy == undefined) {
      return;
    }
    if (cartItemCopy.productIdentifier == undefined) {
      return;
    }

    this.cartService.cartItemSetByProductIdentifierAsync(
      undefined,
      cartItemCopy.productIdentifier,
      cartItemCopy.quantity,
      cartItemCopy.orderUnit,
      cartItemCopy.deliveryInformation
    ).then(
      (shoppingCart: ShoppingCartDto | undefined) => {
        if (shoppingCart) {
          this.acceptQuantityChangeTrigger.next();
          let cartItemNew = shoppingCart.itemList?.find(
            (cartitem) => {
              return this.comparisonService.equalsProductIdentifierByNumberOrGuid(cartItemCopy.productIdentifier, cartitem.productIdentifier);
            }
          );
          if (this.cartPreviewItem != undefined) {
            this.cartPreviewItem.shoppingCartItem = cartItemNew;
            this.acceptQuantityChangeTrigger.next();
          }
        } else {
          this.rejectQuantityChangeTrigger.next();
        }
      }
    );
  }

  public deleteItem() {
    if (this.cartPreviewItem?.productIdentifier == undefined) {
      return;
    }

    let validateCart = ENVIRONMENT.cart.shouldValidateCarts;

    this.cartService.cartItemListDeleteAsync([this.cartPreviewItem.productIdentifier], undefined, validateCart).then(
      (cart: ShoppingCartDto | undefined) => {
        if (!cart) {
          this.toastService.toastItemAdd(new ToastItem({
            type: EToastItemType.Danger,
            title: $localize`:@@CartPreviewItemDeleteInfoErrorTitle:Failure`,
            message: $localize`:@@CartPreviewItemDeleteInfoErrorMessage:Item could not be deleted from shopping cart.`,
            code: ErrorCodeService.formatErrorCode(EError.F170010)
          }));
          return;
        }

        if (ENVIRONMENT.cart.shouldValidateCarts != true) {
          return;
        }

        if (this.cartPreviewItem?.productIdentifier == undefined) {
          return;
        }
        this.cartService.cartItemListValidationDelete([this.cartPreviewItem.productIdentifier]);
      }
    ).catch((ex) => {
      this.toastService.toastItemAdd(new ToastItem({
        type: EToastItemType.Danger,
        title: $localize`:@@CartPreviewItemDeleteInfoErrorTitle:Failure`,
        message: $localize`:@@CartPreviewItemDeleteInfoErrorMessage:Item could not be deleted from shopping cart.`,
        code: JSON.parse(ex.response).errorCode
      }));
    });
  }

}
